import React, {useEffect, useMemo, useState} from "react";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import styles from './index.module.scss'
import AllTasksIcon from '../../../../assets/icon/AllTasksIcon'
import TimeIcon from '../../../../assets/icon/TimeIcon'
import InfoTooltip from '../../../../components/InfoTooltip'
import {getTaskSpentHours} from '../../../../utils/taskSpentTime'

ChartJS.register(ArcElement, Tooltip, Legend);

const GOLDEN_ANGLE = 137.508;
const SATURATION_STEPS = [78, 66, 84];
const LIGHTNESS_STEPS = [48, 61, 42, 68];

interface StatisticsMember {
    user_id: string | number
    full_name?: string | null
    email?: string | null
    tasks_count?: number | string | null
    total_time_spent?: number | string | null
    time_spent?: number | string | null
    spent_time?: number | string | null
    tracked_time?: number | string | null
}

interface StatisticsTask {
    assignee_ids?: Array<string | number> | null
    spendTime?: number | string | null
    spend_time?: number | string | null
    time_spent?: number | string | null
    tracked_time?: number | string | null
    time_tracking?: number | string | null
    minutes_spent?: number | string | null
    time_log?: {minutes_spent?: number | string | null} | null
    timeLog?: {minutes_spent?: number | string | null; minutesSpent?: number | string | null} | null
}

interface ChartUser {
    id: string | number
    name: string
    count: number
    timeSpent: number | null
    backgroundColor: string
}

interface ChartProps {
    chartsData?: StatisticsMember[]
    tasks?: StatisticsTask[]
    activeUser?: string | number | null
    setActiveUser: (userId: string | number | null) => void
}

const toOptionalNumber = (...values: unknown[]) => {
    for (const value of values) {
        if (value == null || value === '') continue

        const numericValue = Number(value)
        if (Number.isFinite(numericValue)) return numericValue
    }

    return null
}

const getMemberSpentTime = (member: StatisticsMember) => {
    const minutesSpent = toOptionalNumber(
        member.total_time_spent,
        member.time_spent,
        member.spent_time,
        member.tracked_time,
    )

    return minutesSpent == null ? null : minutesSpent / 60
}

const getTaskSpentTime = (task: StatisticsTask) => {
    return getTaskSpentHours(task) ?? 0
}

// Both pills stay plain numbers: the icon already says what each one means, and
// spent time is kept in hours (1,5 rather than "1ч. 30м.") so the pair reads as
// one set of figures.
const formatMetricValue = (value: number) => new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 2,
}).format(value)

const getSegmentColor = (index: number) => {
    const hue = (12 + index * GOLDEN_ANGLE) % 360;
    const saturation = SATURATION_STEPS[index % SATURATION_STEPS.length];
    const lightness = LIGHTNESS_STEPS[index % LIGHTNESS_STEPS.length];

    return `hsl(${hue.toFixed(1)}, ${saturation}%, ${lightness}%)`;
}

const Chart = ({chartsData = [], tasks = [], setActiveUser, activeUser}: ChartProps) => {
    const [selected, setSelected] = useState<ChartUser | null>(null);
    const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false);
    // const totalSpendTime = taskList.reduce((accum: number, data: any) => accum + data.time_spent, 0)

    const dataFromApi = useMemo<ChartUser[]>(() => {
        return chartsData.map((user) => ({
            name: user.full_name || user.email || String(user.user_id),
            count: toOptionalNumber(user.tasks_count) ?? 0,
            timeSpent: getMemberSpentTime(user),
            id: user.user_id,
        })).map((item, index) => ({
            ...item,
            backgroundColor: getSegmentColor(index),
        }))
    }, [chartsData]);

    const selectedSpentTime = useMemo(() => {
        if (!selected) return 0
        if (selected.timeSpent != null) return selected.timeSpent

        const selectedId = String(selected.id)

        return tasks.reduce((total, task) => {
            const assigneeIds = Array.isArray(task.assignee_ids)
                ? task.assignee_ids.map(String)
                : []

            if (assigneeIds.length > 0 && !assigneeIds.includes(selectedId)) {
                return total
            }

            return total + getTaskSpentTime(task)
        }, 0)
    }, [selected, tasks])

    const data = {
        labels: dataFromApi.map((item: any) => item.name),
        datasets: [
            {
                data: dataFromApi.map((item: any) => item.count),
                backgroundColor: dataFromApi.map((item: any) => item.backgroundColor),
                borderWidth: 0,
            },
        ],
    };

    const options = {
        cutout: "70%", // делает "дырку" как у тебя
        plugins: {
            legend: {
                display: false,
            },
        },
        onClick: (evt: any, elements: any) => {
            if (elements.length > 0) {
                const index = elements[0].index;
                chooseUser(dataFromApi[index]);
            }
        },
    };

    const chooseUser = (user: ChartUser) => {
        setActiveUser(user?.id)
    }

    useEffect(() => {
        setSelected(dataFromApi.find((user) => String(user.id) === String(activeUser ?? '')) ?? null)
    }, [activeUser, dataFromApi])

    return (
        <div className={styles.chartWidget}>
            <div className={styles.chart} >
                <div style={{ width: "50%" }}>
                    <Doughnut data={data} options={options} />
                </div>
                <div className={styles.chart__circle}>
                    <div
                        className={styles.chart__circle_val}>
                        {selected ? (
                            <>
                                <div className={styles.chart__circleName}>{selected.name}</div>
                                <div className={styles.chart__metrics}>
                                    <InfoTooltip content="Количество задач">
                                        <span
                                            className={styles.chart__metric}
                                            style={{backgroundColor: selected.backgroundColor}}
                                            tabIndex={0}
                                            aria-label={`Количество задач: ${formatMetricValue(selected.count)}`}
                                        >
                                            <AllTasksIcon color="#FFFFFF"/>
                                            <span>{formatMetricValue(selected.count)}</span>
                                        </span>
                                    </InfoTooltip>
                                    <InfoTooltip content="Затраченное время">
                                        <span
                                            className={styles.chart__metric}
                                            style={{backgroundColor: selected.backgroundColor}}
                                            tabIndex={0}
                                            aria-label={`Затраченное время: ${formatMetricValue(selectedSpentTime)}`}
                                        >
                                            <TimeIcon color="#FFFFFF" size={16}/>
                                            <span>{formatMetricValue(selectedSpentTime)}</span>
                                        </span>
                                    </InfoTooltip>
                                </div>
                            </>
                        ) : (
                            <div>Кликни на сегмент</div>
                        )}
                    </div>
                </div>
            </div>
            <div className={`${styles.chart__usersViewport} ${isParticipantsExpanded ? styles.chart__usersViewportExpanded : ''}`}>
                <div className={styles.chart__users}>
                    {dataFromApi.map((item) => (
                        <div key={String(item.id)} className={styles.chart__user} onClick={() => chooseUser(item)}>
                            <div>
                                <div className={styles.chart__user_square} style={{backgroundColor: item.backgroundColor}}/>
                                <span>{item.name}</span>
                            </div>
                            <div>
                                {item.count}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {dataFromApi.length > 4 && (
                <button
                    type="button"
                    className={styles.chart__participantsToggle}
                    onClick={() => setIsParticipantsExpanded((isExpanded) => !isExpanded)}
                >
                    <span>{isParticipantsExpanded ? 'Скрыть список участников' : 'Открыть весь список участников'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path d={isParticipantsExpanded ? 'M3 7.5 6 4.5l3 3' : 'm3 4.5 3 3 3-3'} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            )}
            <div className={styles.chart__total}>
                <div>
                    Всего задач
                </div>
                <div>
                    {dataFromApi.reduce((accum, user) => accum + user.count, 0)}
                </div>
            </div>
        </div>
    );
};

export default Chart;
